import { Edit, Eye, Plus, Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { DataTable } from "../DataTable/DataTable";
import AddNewMedicineModal from "../../shared/AddNewMedicineModel";
import { useAuth } from "../../context/AuthContext";
import { MedicineService } from "../../services/api/medicine.service";
import { toast } from "react-hot-toast";

interface Medicine {
  id: string;
  medicineName: string;
  medicineBrandName: string;
  medicineManufacturer: string;
  medicineSalt: string;
  originCountry: string;
  productForm: string;
  consumeType: string;
}

const StockMedicineView = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [isLoading, setIsLoading] = useState(true);
  const [medicineList, setMedicineList] = useState<Medicine[]>([]);
  const [selectedMedicine, setSelectedMedicine] = useState<Medicine | null>(
    null
  );
  const [showAddMedicineModal, setShowAddMedicineModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [previousPage, setPreviousPage] = useState<number | null>(null);

  const getAllMedicines = async (page = 1, pageSize = 10) => {
    try {
      setIsLoading(true);
      const response = await MedicineService.getMedicineListing({
        page,
        pageSize,
        clinicId: user?.role === "clinic" ? user?.id : "",
        medicalCampId: user?.role === "medicalcamp" ? user?.id : "",
      });
      if (response.success && response.data?.medicines) {
        setMedicineList(response.data.medicines);
        setCurrentPage(response.data.currentPage);
        setTotalPages(response.data.totalPages);
        setNextPage(response.data.nextPage);
        setPreviousPage(response.data.previousPage);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load medicines");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllMedicines(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleEditMedicine = async (id: string, updatedData: Medicine) => {
    try {
      const response = await MedicineService.updateMedicine(id, updatedData);
      if (response.success) {
        toast.success("Medicine updated successfully");
        await getAllMedicines(currentPage, pageSize);
      } else {
        toast.error(response.message || "Failed to update medicine");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update medicine");
    }
  };

  const handleDeleteMedicine = async (id: string) => {
    try {
      const response = await MedicineService.deleteMedicine(id);
      if (response.success) {
        toast.success("Medicine deleted successfully");
        await getAllMedicines(currentPage, pageSize);
      } else {
        toast.error(response.message || "Failed to delete medicine");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete medicine");
    }
  };

  const addMedicineButton = (
    <button
      onClick={() => setShowAddMedicineModal(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      <span>Add Medicine</span>
    </button>
  );

  const columns = [
    {
      key: "name",
      title: "Name",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div
          className="text-sm font-medium line-clamp-2 cursor-default"
          title={item?.medicineName}
        >
          {item.medicineName || "N/A"}
        </div>
      ),
    },
    {
      key: "brandName",
      title: "Brand Name",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div className="text-sm font-medium">
          {item.medicineBrandName || "N/A"}
        </div>
      ),
    },
    {
      key: "manufacturer",
      title: "Manufacturer",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div
          className="text-sm font-medium line-clamp-2 cursor-default"
          title={item?.medicineManufacturer}
        >
          {item.medicineManufacturer || "N/A"}
        </div>
      ),
    },
    {
      key: "medicineSalt",
      title: "Medicine Salt",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div
          className="text-sm font-medium line-clamp-2 cursor-default"
          title={item?.medicineSalt}
        >
          {item.medicineSalt || "N/A"}
        </div>
      ),
    },
    {
      key: "country",
      title: "Country",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div className="text-sm font-medium">{item.originCountry || "N/A"}</div>
      ),
    },
    {
      key: "productForm",
      title: "Product Form",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div className="text-sm font-medium">{item.productForm || "N/A"}</div>
      ),
    },
    {
      key: "consumeType",
      title: "Consume Type",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div className="text-sm font-medium">{item.consumeType || "N/A"}</div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (item: Medicine) => (
        <div className="flex space-x-4">
          {/* <button
            onClick={() => setSelectedMedicine(item)}
            className="text-blue-500 hover:text-blue-700"
          >
            <Eye className="w-5 h-5" />
          </button> */}
          <button
            onClick={() => {
              setSelectedMedicine(item);
              setShowAddMedicineModal(true);
            }}
            className="text-yellow-500 hover:text-yellow-700"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDeleteMedicine(item.id)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="relative">
      {showAddMedicineModal ? (
        <AddNewMedicineModal
          medicineToEdit={selectedMedicine}
          onClose={() => {
            setShowAddMedicineModal(false);
            setSelectedMedicine(null);
          }}
          onSave={async () => {
            await getAllMedicines(currentPage, pageSize);
          }}
        />
      ) : (
        <>
          <DataTable
            data={medicineList}
            columns={columns}
            isLoading={isLoading}
            customFilters={isAdmin && addMedicineButton}
            pagination={{
              currentPage,
              setCurrentPage,
              setTotalPages,
              totalPages,
              pageSize,
              setPageSize,
            }}
          />
        </>
      )}
    </div>
  );
};

export default StockMedicineView;
